import { useEffect, useState } from "react";
import { api, formatApiError, ROLE_LABELS } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, MagnifyingGlass } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

const HR_LEVEL_ROLES = ["hr_manager", "ops_coordinator", "accountant", "supervisor"];
const STAFF_ROLES = new Set(["office_staff", "collection_staff"]);

export default function HrAccess() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  async function load() {
    try { const { data } = await api.get("/employees"); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  async function grant(emp, newRole) {
    try {
      await api.patch(`/employees/${emp.id}`, {
        role: newRole,
        original_role: emp.original_role || emp.role,
      });
      toast.success(`${emp.name}: ${ROLE_LABELS[newRole]} access granted`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function revoke(emp) {
    try {
      const back = emp.original_role || "collection_staff";
      await api.patch(`/employees/${emp.id}`, { role: back });
      toast.success(`${emp.name}: HR access revoked`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const filtered = rows.filter((r) => {
    if (r.role === "super_admin" || r.role === "managing_director") return false; // not editable here
    return !q || r.name?.toLowerCase().includes(q.toLowerCase())
              || r.employee_id?.toLowerCase().includes(q.toLowerCase())
              || r.email?.toLowerCase().includes(q.toLowerCase());
  });

  if (user?.role !== "super_admin") {
    return (
      <div className="p-10 text-sm text-zinc-400">
        Restricted. HR Access is manageable by the super-administrator only.
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#E50914] mb-2">Access Control</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">HR Platform Access</h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Promote any saved staff to platform access (HR manager / Ops / Accountant / Supervisor).
            These users can sign in via the <code className="text-[#E50914]">/hr</code> portal
            and manage the workforce — they do <b>not</b> get administrator privileges.
          </p>
        </div>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-3 text-zinc-500" />
          <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search staff…"
                 className="w-64 pl-8 bg-[#16161C] border-[#2D2D35] h-10" data-testid="hra-search" />
        </div>
      </div>

      <div className="bg-[#16161C] border border-[#2D2D35] rounded-md overflow-hidden">
        <table className="w-full" data-testid="hr-access-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#2D2D35]">
              <th className="text-left p-3">Emp ID</th>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Current Role</th>
              <th className="text-left p-3">Original</th>
              <th className="text-left p-3">Promote to</th>
              <th className="text-right p-3">Platform Access</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => {
              const hasAccess = HR_LEVEL_ROLES.includes(e.role);
              const isStaff = STAFF_ROLES.has(e.role);
              return (
                <tr key={e.id} className="border-b border-[#2D2D35]/60 hover:bg-[#1C1C24]">
                  <td className="p-3 font-mono text-xs">{e.employee_id}</td>
                  <td className="p-3">
                    <div className="text-white text-sm">{e.name}</div>
                    <div className="text-[10px] text-zinc-500">{e.email}</div>
                  </td>
                  <td className="p-3 text-xs">
                    <span className={`px-2 py-1 rounded ${hasAccess ? "bg-[#22C55E]/15 text-[#22C55E]" : "bg-[#0D0D10] border border-[#2D2D35] text-zinc-300"}`}>
                      {ROLE_LABELS[e.role] || e.role}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-zinc-400">{e.original_role ? ROLE_LABELS[e.original_role] : "—"}</td>
                  <td className="p-3 text-xs">
                    <Select value="" onValueChange={(v) => grant(e, v)} disabled={hasAccess && false}>
                      <SelectTrigger className="h-8 w-44 bg-[#0D0D10] border-[#2D2D35] text-xs" data-testid={`hra-role-${e.employee_id}`}>
                        <SelectValue placeholder={hasAccess ? "Change role…" : "Grant access…"} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                        {HR_LEVEL_ROLES.map((r) => (
                          <SelectItem key={r} value={r} disabled={r === e.role}>{ROLE_LABELS[r]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      {hasAccess && (
                        <button onClick={() => revoke(e)} data-testid={`hra-revoke-${e.employee_id}`}
                                className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FF3B30]">
                          Revoke
                        </button>
                      )}
                      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded ${
                        hasAccess ? "bg-[#22C55E]/15 text-[#22C55E]" :
                        isStaff ? "bg-zinc-700/30 text-zinc-400" :
                        "bg-[#F59E0B]/15 text-[#F59E0B]"
                      }`}>
                        <ShieldCheck size={11} weight={hasAccess ? "fill" : "regular"} />
                        {hasAccess ? "GRANTED" : isStaff ? "STAFF ONLY" : "—"}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!filtered.length && <tr><td colSpan="6" className="text-center text-zinc-500 p-8 text-sm">No staff match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
